import { WhereClauseModalContent } from '@modules/common/components/WhereClauseField/WhereClauseModalContent';
import { WhereClausePreviewProps } from '@modules/common/components/WhereClausePreview/WhereClausePreview';
import { isEmptyWhereClause } from '@modules/common/utils';
import { QueryBuilderActionsHandle } from '@modules/queryBuilder/components/QueryBuilder/QueryBuilder';
import {
  alpha,
  Box,
  Dialog,
  Paper,
  Stack,
  styled,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useControlled,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { ReactNode, useRef } from 'react';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { WhereClausePreview } from '../WhereClausePreview';

export interface WhereClauseFieldProps {
  whereClause?: WhereClause | null;
  whereClauseForPreview?: WhereClause | null;
  onChange: (value: {
    whereClause: WhereClause | null;
    isQuickFilter?: boolean;
  }) => void;
  label: ReactNode;
  isDisabled?: boolean;
  modalTitle?: ReactNode;
  helperTextNode?: ReactNode;
  shouldOpenModal?: boolean;
  onShouldOpenModelChange?: (shouldOpen: boolean) => void;
  forceEnableExecuteButton?: boolean;
  modalTestId?: string;
  whereClausePreviewProps?: Partial<WhereClausePreviewProps>;
  addFilterSxProps?: SxProps<Theme>;
  canEdit?: boolean;
  disableOpenModal?: boolean;
}

const StyledPaper = styled(Paper)`
  & {
    position: absolute;
    top: 50px;
  }
`;

export const WhereClauseField = ({
  whereClause,
  whereClauseForPreview,
  onChange,
  label,
  isDisabled = false,
  modalTitle,
  helperTextNode,
  shouldOpenModal: shouldOpenModalProp,
  onShouldOpenModelChange,
  forceEnableExecuteButton,
  modalTestId,
  whereClausePreviewProps,
  addFilterSxProps,
  canEdit = true,
  disableOpenModal = false,
}: WhereClauseFieldProps) => {
  const theme = useTheme();

  const [shouldOpenQBModal, setShouldOpenQBModal] = useControlled({
    controlled: shouldOpenModalProp,
    default: false,
    name: 'WhereClauseField',
    state: 'shouldOpenQBModal',
  });

  const queryBuilderRef = useRef<QueryBuilderActionsHandle>(null);

  const openWhereClauseModal = () => {
    setShouldOpenQBModal(true);
    onShouldOpenModelChange?.(true);
  };

  const closeWhereClauseModal = (
    _?: {},
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (reason === 'backdropClick') {
      return;
    }

    setShouldOpenQBModal(false);
    onShouldOpenModelChange?.(false);
    queryBuilderRef.current?.resetQuery(whereClause ?? undefined);
  };

  const whereClauseToPreview = whereClauseForPreview ?? whereClause;
  const shouldShowPreview =
    whereClauseToPreview && !isEmptyWhereClause(whereClauseToPreview);

  const getCursor = () => {
    if (isDisabled) {
      return 'not-allowed';
    }
    if (disableOpenModal) {
      return 'initial';
    }
    return 'pointer';
  };

  const isReadOnly =
    !shouldShowPreview || isDisabled || disableOpenModal || !canEdit;

  return (
    <Stack spacing={1.5}>
      {label}
      <Tooltip
        arrow
        title={isReadOnly ? null : 'Click to edit filters'}
        placement="top"
        disableInteractive
      >
        <Box>
          <Box
            onClick={openWhereClauseModal}
            sx={{
              pointerEvents:
                isDisabled || disableOpenModal ? 'none' : 'initial',
              cursor: getCursor(),
              opacity: isDisabled ? 0.5 : 1,
            }}
            aria-disabled={isDisabled ? 'true' : 'false'}
            aria-label="open-query-builder-modal-button"
          >
            {shouldShowPreview ? (
              <WhereClausePreview
                whereClause={whereClauseForPreview ?? whereClause}
                sxProps={{
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.1),
                  },
                  ...whereClausePreviewProps?.sxProps,
                }}
                {...whereClausePreviewProps}
              />
            ) : (
              <Typography
                sx={{
                  px: 2.5,
                  py: 2,
                  maxHeight: '150px',
                  overflow: 'auto',
                  borderRadius: 1,
                  border: '1px solid',
                  borderStyle: 'dashed',
                  borderColor: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.05),
                  ':hover': {
                    background: alpha(theme.palette.primary.main, 0.1),
                  },
                  ...addFilterSxProps,
                }}
              >
                + Add Filter
              </Typography>
            )}
          </Box>
          {helperTextNode}
        </Box>
      </Tooltip>
      <Dialog
        data-testid={modalTestId}
        open={shouldOpenQBModal}
        fullWidth={true}
        maxWidth="md"
        keepMounted={true}
        onClose={closeWhereClauseModal}
        PaperComponent={StyledPaper}
      >
        <WhereClauseModalContent
          ref={queryBuilderRef}
          whereClause={whereClause ?? undefined}
          onChange={onChange}
          onClose={closeWhereClauseModal}
          shouldSkipRender={!shouldOpenQBModal}
          modalTitle={modalTitle}
          forceEnableExecuteButton={forceEnableExecuteButton}
          readOnly={!canEdit}
        />
      </Dialog>
    </Stack>
  );
};
